import React, { useMemo, useState } from "react";
import { Subject } from "./types";
import Edit from "../../assets/icons/Edit";
import Delete from "../../assets/icons/Delete";
import ArrowDown from "../../assets/icons/ArrowDown";

interface SubjectsTableProps {
  onEdit?: (subject: Subject) => void;
  onDelete?: (subject: Subject) => void;
  data?: Subject[];
  searchQuery?: string;
  sortBy?: keyof Subject;
  sortDir?: "asc" | "desc";
  loading?: boolean;
}

export const mockSubjects: Subject[] = [
  {
    id: "1",
    name: "Программирование",
    disciplineCipher: "CS101",
    disciplineCode: "PROG101",
    creditAmount: 5,
    description: "Основы программирования на Python",
    specialities: ["Программная инженерия", "Информационные системы"],
    prerequisiteIds: ["2", "3"],
    relatedSubjectIds: ["2"],
  },
  {
    id: "2",
    name: "Математический анализ",
    disciplineCipher: "MATH101",
    disciplineCode: "MA101",
    creditAmount: 6,
    description: "Дифференциальное и интегральное исчисление",
    specialities: ["Программная инженерия", "Прикладная математика"],
    prerequisiteIds: [],
    relatedSubjectIds: [],
  },
  {
    id: "3",
    name: "Базы данных",
    disciplineCipher: "CS201",
    disciplineCode: "DB201",
    creditAmount: 4,
    description: "Проектирование и работа с реляционными БД",
    specialities: ["Программная инженерия"],
    prerequisiteIds: ["1"],
    relatedSubjectIds: [],
  },
];

export default function SubjectsTable({
  onEdit,
  onDelete,
  data,
  searchQuery = "",
  sortBy,
  sortDir = "asc",
  loading = false,
}: SubjectsTableProps) {
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [expandedPrerequisites, setExpandedPrerequisites] = useState<Record<string, boolean>>({});
  const [expandedRelated, setExpandedRelated] = useState<Record<string, boolean>>({});

  const transitionClasses = {
    buttonHover: "transition-opacity duration-200 ease-in-out",
    rowHover: "transition-colors duration-200 ease-in-out",
    arrowRotation: "transition-transform duration-300 ease-in-out"
  };

  const toggleRow = (id: string): void => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const togglePrerequisites = (id: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    setExpandedPrerequisites((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const toggleRelated = (id: string, e: React.MouseEvent): void => {
    e.stopPropagation();
    setExpandedRelated((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const source = data ?? mockSubjects;
  const isEmpty = source.length === 0 && !loading;

  const processedData = useMemo<Subject[]>(() => {
    const q = searchQuery.trim().toLowerCase();
    let filtered = source.filter((subject) => {
      if (!q) return true;
      return [
        subject.name,
        subject.disciplineCipher,
        subject.disciplineCode,
        String(subject.creditAmount),
        subject.description,
        subject.specialities.join(" "),
      ].some(val => String(val ?? '').toLowerCase().includes(q));
    });

    if (!sortBy) return filtered;

    const sorted = [...filtered].sort((a, b) => {
      const av = (a[sortBy] as any) ?? '';
      const bv = (b[sortBy] as any) ?? '';
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av;
      }
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return sortDir === 'asc' ? -1 : 1;
      if (as > bs) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [source, searchQuery, sortBy, sortDir]);

  const findSubjectById = (id: string): Subject | undefined => {
    return source.find(s => s.id === id);
  };

  const renderButtons = (subject: Subject) => {
    return (
      <div className="flex items-center justify-center gap-2">
        <button
          type="button"
          className={`p-[6px] rounded text-valley-gray opacity-20 hover:opacity-100 bg-gray-600/20 ${transitionClasses.buttonHover}`}
          onClick={(e) => {
            e.stopPropagation();
            onEdit ? onEdit(subject) : console.log('edit', subject);
          }}
        >
          <Edit/>
        </button>
        <button
          type="button"
          className={`p-[6px] rounded text-valley-red opacity-20 hover:opacity-100 bg-red-600/20 ${transitionClasses.buttonHover}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete ? onDelete(subject) : console.log('delete', subject);
          }}
        >
          <Delete/>
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="w-full mt-4 py-8 text-center text-valley-gray">
        Загрузка...
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Десктопная версия */}
      <div className="hidden lg:block overflow-x-auto">
        <div className="max-h-[70vh] overflow-y-auto mt-4 rounded-lg">
          <table className="w-full border-collapse text-sm font-medium">
            <thead className="sticky top-0 bg-white z-10">
              <tr className="bg-white">
                <th className="text-valley-gray font-medium px-3 py-2 border-b text-left w-12"></th>
                <th className="text-valley-gray font-medium px-3 py-2 border-b text-left">Название дисциплины</th>
                <th className="text-valley-gray font-medium px-3 py-2 border-b text-left">Шифр дисц.</th>
                <th className="text-valley-gray font-medium px-3 py-2 border-b text-left">Код дисц.</th>
                <th className="text-valley-gray font-medium px-3 py-2 border-b text-left">Кол-во кредитов</th>
                <th className="text-valley-gray font-medium px-3 py-2 border-b text-left">Описание</th>
                <th className="text-valley-gray font-medium px-3 py-2 border-b text-left">Привязка к специальности</th>
                <th className="text-valley-gray font-medium px-3 py-2 border-b text-center w-24">Действия</th>
              </tr>
            </thead>

            <tbody className={'select-none'}>
              {isEmpty ? (
                <tr>
                  <td colSpan={8} className="px-3 py-8 text-center text-valley-gray">
                    Нет предметов. Создайте первый предмет с помощью кнопки «Создать предмет».
                  </td>
                </tr>
              ) : processedData.map((subject) => (
                <React.Fragment key={subject.id}>
                  <tr
                    className={`${expandedRows[subject.id] ? 'bg-[#F0F3FA]' : 'bg-white'} cursor-pointer ${transitionClasses.rowHover}`}
                    onClick={() => toggleRow(subject.id)}
                  >
                    <td className="px-3 py-2 border-b ">
                      <div className={`border-[1px] border-valley-light-gray p-2 flex items-center justify-center rounded-[6px] ${expandedRows[subject.id] ? 'border-valley-main bg-valley-main text-white' : ''} text-valley-light-gray`}>
                        <div className={`${expandedRows[subject.id] ? 'rotate-180' : 'rotate-0' } ${transitionClasses.arrowRotation}`}>
                            <ArrowDown />
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2 border-b text-black font-medium">{subject.name}</td>
                    <td className="px-3 py-2 border-b text-black font-medium">{subject.disciplineCipher}</td>
                    <td className="px-3 py-2 border-b text-black font-medium">{subject.disciplineCode}</td>
                    <td className="px-3 py-2 border-b text-black font-medium text-center">{subject.creditAmount}</td>
                    <td className="px-3 py-2 border-b text-black font-medium">{subject.description}</td>
                    <td className="px-3 py-2 border-b text-black font-medium">{subject.specialities.join(", ")}</td>
                    <td className="px-3 py-2 border-b">
                      {renderButtons(subject)}
                    </td>
                  </tr>

                  {expandedRows[subject.id] && (
                    <>
                      {/* Строка "Пререквизит" */}
                      <tr className="bg-[#3BBC300F]">
                        <td className="px-3 py-2 border-b"></td>
                        <td
                          colSpan={7}
                          className="px-3 py-2 border-b cursor-pointer "
                          onClick={(e) => togglePrerequisites(subject.id, e)}
                        >
                          <div className="flex items-center gap-2 text-valley-gray font-semibold">
                            Пререквизит
                              <div className={`${expandedPrerequisites[subject.id] ? 'rotate-180' : 'rotate-0'} ${transitionClasses.arrowRotation}`}>
                                  <ArrowDown />
                              </div>
                          </div>
                        </td>
                      </tr>
                      
                      {expandedPrerequisites[subject.id] && subject.prerequisiteIds.length > 0 && (
                        <>
                          {subject.prerequisiteIds.map((prereqId) => {
                            const prereq = findSubjectById(prereqId);
                            if (!prereq) return null;
                            return (
                              <tr key={prereqId} className="bg-[#3BBC300F]">
                                <td className="px-3 py-2 border-b"></td>
                                <td className="px-3 py-2 border-b text-black font-medium">{prereq.name}</td>
                                <td className="px-3 py-2 border-b text-black font-medium">{prereq.disciplineCipher}</td>
                                <td className="px-3 py-2 border-b text-black font-medium">{prereq.disciplineCode}</td>
                                <td className="px-3 py-2 border-b text-black font-medium text-center">{prereq.creditAmount}</td>
                                <td className="px-3 py-2 border-b text-black font-medium">{prereq.description}</td>
                                <td className="px-3 py-2 border-b text-black font-medium">{prereq.specialities.join(", ")}</td>
                                <td className="px-3 py-2 border-b"></td>
                              </tr>
                            );
                          })}
                        </>
                      )}

                      {expandedPrerequisites[subject.id] && subject.prerequisiteIds.length === 0 && (
                        <tr className="bg-[#3BBC300F]">
                          <td className="px-3 py-2 border-b"></td>
                          <td colSpan={7} className="px-6 py-2 border-b text-sm text-valley-gray italic">
                            Нет пререквизитов
                          </td>
                        </tr>
                      )}

                      {/* Строка "Старый/новый предмет" */}
                      <tr className="bg-[#F585000F]">
                        <td className="px-3 py-2 border-b"></td>
                        <td
                          colSpan={7}
                          className="px-3 py-2 border-b cursor-pointer "
                          onClick={(e) => toggleRelated(subject.id, e)}
                        >
                          <div className="flex items-center gap-2 text-valley-gray font-semibold">
                            Старый/новый предмет
                              <div className={`${expandedRelated[subject.id] ? 'rotate-180' : 'rotate-0'} ${transitionClasses.arrowRotation}`}>
                                  <ArrowDown />
                              </div>
                          </div>
                        </td>
                      </tr>

                      {expandedRelated[subject.id] && subject.relatedSubjectIds.length > 0 && (
                        <>
                          {subject.relatedSubjectIds.map((relatedId) => {
                            const related = findSubjectById(relatedId);
                            if (!related) return null;
                            return (
                              <tr key={relatedId} className="bg-[#F585000F]">
                                <td className="px-3 py-2 border-b"></td>
                                <td className="px-3 py-2 border-b text-black font-medium">{related.name}</td>
                                <td className="px-3 py-2 border-b text-black font-medium">{related.disciplineCipher}</td>
                                <td className="px-3 py-2 border-b text-black font-medium">{related.disciplineCode}</td>
                                <td className="px-3 py-2 border-b text-black font-medium text-center">{related.creditAmount}</td>
                                <td className="px-3 py-2 border-b text-black font-medium">{related.description}</td>
                                <td className="px-3 py-2 border-b text-black font-medium">{related.specialities.join(", ")}</td>
                                <td className="px-3 py-2 border-b"></td>
                              </tr>
                            );
                          })}
                        </>
                      )}

                      {expandedRelated[subject.id] && subject.relatedSubjectIds.length === 0 && (
                        <tr className="bg-[#F585000F]">
                          <td className="px-3 py-2 border-b"></td>
                          <td colSpan={7} className="px-6 py-2 border-b text-sm text-valley-gray italic">
                            Нет связанных предметов
                          </td>
                        </tr>
                      )}
                    </>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Мобильная версия (карточки) */}
      <div className="lg:hidden mt-4 space-y-4 max-h-[70vh] overflow-y-auto">
        {isEmpty ? (
          <div className="py-8 text-center text-valley-gray">
            Нет предметов. Создайте первый предмет с помощью кнопки «Создать предмет».
          </div>
        ) : processedData.map((subject) => (
          <div key={subject.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div
              className={`px-4 py-3 font-semibold cursor-pointer hover:bg-gray-100 flex justify-between items-center ${transitionClasses.rowHover}`}
              onClick={() => toggleRow(subject.id)}
            >
              <span className="truncate pr-3 text-black">{subject.name}</span>
              <div className={`${expandedRows[subject.id] ? 'rotate-180' : 'rotate-0'} ${transitionClasses.arrowRotation} text-valley-gray`}>
                <ArrowDown />
              </div>
            </div>

            {expandedRows[subject.id] && (
              <div className="p-4 space-y-3 text-sm text-black">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <span className="font-semibold text-valley-gray">Шифр дисц.:</span>
                    <div className="mt-1">{subject.disciplineCipher}</div>
                  </div>
                  <div>
                    <span className="font-semibold text-valley-gray">Код дисц.:</span>
                    <div className="mt-1">{subject.disciplineCode}</div>
                  </div>
                </div>

                <div>
                  <span className="font-semibold text-valley-gray">Кол-во кредитов:</span>
                  <div className="mt-1">{subject.creditAmount}</div>
                </div>

                <div>
                  <span className="font-semibold text-valley-gray">Описание:</span>
                  <div className="mt-1">{subject.description}</div>
                </div>

                <div>
                  <span className="font-semibold text-valley-gray">Привязка к специальности:</span>
                  <div className="mt-1">{subject.specialities.join(", ")}</div>
                </div>

                {/* Пререквизит */}
                <div className="border-t pt-3 bg-[#3BBC300F]">
                  <div
                    className="flex items-center gap-2 font-semibold text-valley-gray cursor-pointer"
                    onClick={(e) => togglePrerequisites(subject.id, e)}
                  >
                    <div className={`${expandedPrerequisites[subject.id] ? 'rotate-180' : 'rotate-0'} ${transitionClasses.arrowRotation}`}>
                      <ArrowDown />
                    </div>
                    Пререквизит
                  </div>
                  {expandedPrerequisites[subject.id] && (
                    <div className="mt-2 space-y-2 bg-[#3BBC300F]">
                      {subject.prerequisiteIds.length > 0 ? (
                        subject.prerequisiteIds.map((prereqId) => {
                          const prereq = findSubjectById(prereqId);
                          if (!prereq) return null;
                          return (
                            <div key={prereqId} className=" p-3 rounded space-y-2">
                              <div>
                                <span className="font-semibold text-valley-gray">Название:</span>
                                <div className="mt-1">{prereq.name}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <span className="font-semibold text-valley-gray">Шифр:</span>
                                  <div className="mt-1">{prereq.disciplineCipher}</div>
                                </div>
                                <div>
                                  <span className="font-semibold text-valley-gray">Код:</span>
                                  <div className="mt-1">{prereq.disciplineCode}</div>
                                </div>
                              </div>
                              <div>
                                <span className="font-semibold text-valley-gray">Кредиты:</span>
                                <div className="mt-1">{prereq.creditAmount}</div>
                              </div>
                              <div>
                                <span className="font-semibold text-valley-gray">Описание:</span>
                                <div className="mt-1">{prereq.description}</div>
                              </div>
                              <div>
                                <span className="font-semibold text-valley-gray">Специальности:</span>
                                <div className="mt-1">{prereq.specialities.join(", ")}</div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-valley-gray italic">Нет пререквизитов</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Старый/новый предмет */}
                <div className="border-t pt-3 bg-[#F585000F]">
                  <div
                    className=" flex items-center gap-2 font-semibold text-valley-gray cursor-pointer"
                    onClick={(e) => toggleRelated(subject.id, e)}
                  >
                    <div className={`${expandedRelated[subject.id] ? 'rotate-180' : 'rotate-0'} ${transitionClasses.arrowRotation}`}>
                      <ArrowDown />
                    </div>
                    Старый/новый предмет
                  </div>
                  {expandedRelated[subject.id] && (
                    <div className="mt-2 space-y-2">
                      {subject.relatedSubjectIds.length > 0 ? (
                        subject.relatedSubjectIds.map((relatedId) => {
                          const related = findSubjectById(relatedId);
                          if (!related) return null;
                          return (
                            <div key={relatedId} className="bg-[#F585000F] p-3 rounded space-y-2">
                              <div>
                                <span className="font-semibold text-valley-gray">Название:</span>
                                <div className="mt-1">{related.name}</div>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <span className="font-semibold text-valley-gray">Шифр:</span>
                                  <div className="mt-1">{related.disciplineCipher}</div>
                                </div>
                                <div>
                                  <span className="font-semibold text-valley-gray">Код:</span>
                                  <div className="mt-1">{related.disciplineCode}</div>
                                </div>
                              </div>
                              <div>
                                <span className="font-semibold text-valley-gray">Кредиты:</span>
                                <div className="mt-1">{related.creditAmount}</div>
                              </div>
                              <div>
                                <span className="font-semibold text-valley-gray">Описание:</span>
                                <div className="mt-1">{related.description}</div>
                              </div>
                              <div>
                                <span className="font-semibold text-valley-gray">Специальности:</span>
                                <div className="mt-1">{related.specialities.join(", ")}</div>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="text-valley-gray italic">Нет связанных предметов</div>
                      )}
                    </div>
                  )}
                </div>

                {/* Кнопки действий */}
                <div className="flex flex-col sm:flex-row gap-2 pt-3 border-t">
                  <button
                    type="button"
                    className={`flex-1 px-3 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 ${transitionClasses.buttonHover}`}
                    onClick={() => onEdit ? onEdit(subject) : console.log('edit', subject)}
                  >
                    Редактировать
                  </button>
                  <button
                    type="button"
                    className={`flex-1 px-3 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 ${transitionClasses.buttonHover}`}
                    onClick={() => onDelete ? onDelete(subject) : console.log('delete', subject)}
                  >
                    Удалить
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}